import { Clock3, Mail, MapPin, Phone } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { TourForm } from "../components/TourForm";
import { SITE } from "../data/content";
import { useLang } from "../i18n";

const CARD_COLORS = [
  { bg: "#FDE7E7", fg: "#D7352B" },
  { bg: "#E3EFFC", fg: "#2E7CD6" },
  { bg: "#E6F4EA", fg: "#3D9B4F" },
  { bg: "#FFF2C4", fg: "#B98A00" },
];

export default function Contact() {
  const { t } = useLang();
  const CARDS = [
    { icon: MapPin, title: t("card_visit"), lines: [SITE.address], href: "https://www.google.com/maps/search/?api=1&query=207+Emerson+Drive+NW+Palm+Bay+FL+32907", testId: "contact-address-card" },
    { icon: Phone, title: t("card_call"), lines: [SITE.phoneDisplay], href: SITE.phoneHref, testId: "contact-phone-card" },
    { icon: Mail, title: t("card_email"), lines: [SITE.email], href: `mailto:${SITE.email}`, testId: "contact-email-card" },
    { icon: Clock3, title: t("card_hours"), lines: [SITE.hours], testId: "contact-hours-card" },
  ];

  return (
    <>
      <section className="pt-44 pb-16 bg-cream grain relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-sage blur-3xl" />
        <div className="absolute top-32 left-1/4 w-40 h-40 rounded-full bg-amber/15 blur-2xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 relative">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-tangerine">{t("contact_overline")}</p>
            <h1 className="mt-4 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-forest tracking-tight max-w-3xl leading-tight">
              {t("contact_h1")}
            </h1>
            <p className="mt-6 text-bark text-base sm:text-lg max-w-2xl leading-relaxed">{t("contact_sub")}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {CARDS.map((card, i) => {
              const col = CARD_COLORS[i % CARD_COLORS.length];
              const inner = (
                <>
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5" style={{ background: col.bg }}>
                    <card.icon className="w-6 h-6" style={{ color: col.fg }} />
                  </div>
                  <h3 className="font-display font-bold text-lg text-forest">{card.title}</h3>
                  {card.lines.map((l) => <p key={l} className="text-sm text-bark mt-1.5 leading-relaxed">{l}</p>)}
                </>
              );
              return (
                <Reveal key={card.title} delay={i * 0.07}>
                  {card.href ? (
                    <a href={card.href} target={card.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={card.testId}
                       className="block rounded-3xl bg-cream border border-bone p-7 h-full hover:-translate-y-1.5 hover:shadow-xl hover:shadow-forest/10 transition-all duration-400">
                      {inner}
                    </a>
                  ) : (
                    <div data-testid={card.testId} className="rounded-3xl bg-cream border border-bone p-7 h-full">{inner}</div>
                  )}
                </Reveal>
              );
            })}
          </div>

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch">
            <Reveal className="h-full">
              <div className="rounded-[2rem] overflow-hidden border border-bone shadow-xl shadow-forest/10 h-full min-h-[24rem]">
                <iframe
                  data-testid="contact-map"
                  title="Map to The Learning Tree Academy, 207 Emerson Drive NW, Palm Bay FL"
                  src={SITE.mapEmbed}
                  className="w-full h-full min-h-[24rem]"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </Reveal>
            <Reveal delay={0.12} className="h-full">
              <div className="rounded-[2rem] bg-forest p-8 sm:p-10 h-full relative overflow-hidden grain">
                <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-tree/30 blur-3xl" />
                <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-tangerine/20 blur-3xl" />
                <div className="relative">
                  <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-cream tracking-tight">{t("contact_panel_h2")}</h2>
                  <p className="mt-3 text-sm text-cream/70">{t("contact_panel_sub")}</p>
                  <div className="mt-8 rounded-3xl bg-white p-6 sm:p-8">
                    <TourForm compact />
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
